import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

function DrillCatalogPage({ basket, onAddToBasket, onUpdateBasketQuantity, onRemoveFromBasket, user, onShowLogin }) {
    const navigate = useNavigate();
    const [isAppMenuOpen, setIsAppMenuOpen] = useState(false);
    const [drillCategories, setDrillCategories] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [pdfModalOpen, setPdfModalOpen] = useState(false);

    const [openSections, setOpenSections] = useState({});
    const [selectKey, setSelectKey] = useState({});
    const [dropdownSearch, setDropdownSearch] = useState({});
    const [dropdownOpen, setDropdownOpen] = useState({});
    const [localSelections, setLocalSelections] = useState({}); // Temporary selections before adding to basket

    // Contact form state
    const [contactInfo, setContactInfo] = useState({
        name: '',
        company: '',
        email: '',
        phone: '',
        message: ''
    });

    const MAX_QUANTITY_PER_ITEM = 5;

    const getDrillImagePath = (partNumber) => {
        if (!partNumber) return null;
        const sanitized = partNumber.trim()
            .replace(/[<>:"/\\|?*\x00-\x1F]/g, "_")
            .replace(/\s+/g, " ");
        return `/MM_Drills/images/${sanitized}.png`;
    };

    const FALLBACK_IMAGE = "images/logo/MM Logo.jpg";

    const handleImageError = (e) => {
        e.target.onerror = null;
        e.target.src = FALLBACK_IMAGE;
    };

    // Parse drill catalog data from text file
    useEffect(() => {
        fetch('/MM_Drills/excel_text.txt')
            .then(res => res.text())
            .then(text => {
                const lines = text.split('\n').map(l => l.trim()).filter(l => l);
                const categories = [];
                let currentCategory = null;

                for (const line of lines) {
                    if (line.endsWith('(HEADER)')) {
                        currentCategory = { name: line.replace(' (HEADER)', ''), items: [] };
                        categories.push(currentCategory);
                    } else if (currentCategory) {
                        const parts = line.split('\t');
                        const partNumber = parts[1] ? parts[1].trim() : '';
                        if (partNumber) {
                            currentCategory.items.push(partNumber);
                        }
                    }
                }

                setDrillCategories(categories);
                setIsLoadingData(false);
            })
            .catch(err => {
                console.error('Failed to load drill catalog data:', err);
                setIsLoadingData(false);
            });
    }, []);

    const toggleSection = (sectionId) => {
        setOpenSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            const dropdowns = document.querySelectorAll('[data-dropdown]');
            dropdowns.forEach(dropdown => {
                if (!dropdown.contains(e.target)) {
                    const category = dropdown.getAttribute('data-dropdown');
                    setDropdownOpen(prev => ({ ...prev, [category]: false }));
                    setDropdownSearch(prev => ({ ...prev, [category]: '' }));
                }
            });
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDropdownChange = (category, item) => {
        setLocalSelections(prev => {
            if (!item) return prev;
            const newSelections = { ...prev };
            const categoryItems = newSelections[category] || {};
            if (categoryItems[item]) return prev;
            newSelections[category] = { ...categoryItems, [item]: 1 };
            return newSelections;
        });
        setSelectKey(prev => ({ ...prev, [category]: (prev[category] || 0) + 1 }));
        setDropdownSearch(prev => ({ ...prev, [category]: '' }));
        setDropdownOpen(prev => ({ ...prev, [category]: false }));
    };

    const handleRemoveItem = (category, item) => {
        setLocalSelections(prev => {
            const newSelections = { ...prev };
            const categoryItems = newSelections[category] || {};
            delete categoryItems[item];
            if (Object.keys(categoryItems).length === 0) {
                delete newSelections[category];
            } else {
                newSelections[category] = categoryItems;
            }
            return newSelections;
        });
    };

    const handleQuantityChange = (category, item, delta) => {
        setLocalSelections(prev => {
            if (!prev[category] || !prev[category][item]) return prev;
            const current = prev[category][item];
            const newQty = Math.max(1, Math.min(MAX_QUANTITY_PER_ITEM, current + delta));
            const newSelections = { ...prev };
            newSelections[category] = { ...newSelections[category] };
            newSelections[category][item] = newQty;
            return newSelections;
        });
    };

    const handleContactChange = (e) => {
        const { name, value } = e.target;
        setContactInfo(prev => ({ ...prev, [name]: value }));
    };

    // Count total selections across all categories
    const totalSelections = Object.values(localSelections).reduce(
        (sum, items) => sum + Object.keys(items).length, 0
    );

    // Check if contact form is valid
    const isContactValid = contactInfo.name.trim() && contactInfo.email.trim();

    // Build products array for basket
    const buildProductsArray = () => {
        const products = [];
        Object.entries(localSelections).forEach(([category, items]) => {
            Object.entries(items).forEach(([itemName, quantity]) => {
                products.push({
                    name: itemName,
                    quantity: quantity,
                    category: category,
                    partNumber: itemName
                });
            });
        });
        return products;
    };

    const handleAddAllToBasket = () => {
        const products = buildProductsArray();
        if (products.length === 0) {
            alert('Please select at least one product.');
            return;
        }
        if (!isContactValid) {
            alert('Please fill in your Name and Email.');
            return;
        }

        // Create a single drill-enquiry basket item containing contact info + all products
        const enquiryItem = {
            id: `drill-enquiry-${Date.now()}`,
            name: 'Drill Catalogue Enquiry',
            listingType: 'drill-enquiry',
            price: '0',
            quantity: 1,
            image_url_1: 'images/logo/MM Logo.jpg',
            listing_number: `DRILL-ENQ-${Date.now()}`,
            contactInfo: contactInfo,
            products: products
        };

        onAddToBasket(enquiryItem, 1, null, true);

        // Clear local selections and contact form
        setLocalSelections({});
        setContactInfo({ name: '', company: '', email: '', phone: '', message: '' });
        setOpenSections({});
        
        // Navigate to basket page
        navigate('/basket');
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <img
                                src="images/logo/MM Logo.jpg"
                                alt="Logo"
                                className="h-9 w-9 rounded-lg cursor-pointer shadow-sm border border-gray-200"
                                onClick={() => navigate('/')}
                            />
                            <div>
                                <h1 className="text-2xl font-black text-gray-900 tracking-tighter uppercase leading-none">Drill Catalog</h1>
                                <p className="text-[10px] text-blue-600 font-bold uppercase tracking-[0.2em] mt-1">Mining Drills & Spare Parts</p>
                                <button
                                    onClick={() => navigate('/')}
                                    className="mt-2 text-sm text-gray-600 hover:text-blue-600 transition-colors flex items-center gap-1"
                                >
                                    <i className="fas fa-arrow-left"></i>
                                    Back to Home
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            {/* Basket Icon with Count */}
                            <button
                                onClick={() => navigate('/basket')}
                                className="relative p-2 text-gray-900 hover:text-blue-600 transition-colors"
                                title="View Basket"
                            >
                                <i className="fas fa-shopping-basket text-xl"></i>
                                {(basket?.length > 0 || totalSelections > 0) && (
                                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                                        {basket?.reduce((acc, item) => acc + item.quantity, 0) + totalSelections}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => setIsAppMenuOpen(true)}
                                className="p-2 text-gray-900 hover:text-blue-600 transition-colors"
                            >
                                <i className="fas fa-bars text-xl"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* App Navigation Sidebar (Slide-in) */}
            {isAppMenuOpen && (
                <div className="fixed inset-0 z-[100] flex justify-end">
                    <div className="absolute inset-0 bg-black/30 transition-opacity" onClick={() => setIsAppMenuOpen(false)}></div>
                    <div className="relative w-4/5 max-w-[400px] bg-white h-full shadow-2xl flex flex-col transform transition-transform duration-300 animate-slide-in-right overflow-hidden border-l border-gray-200">
                        <div className="p-4 border-b flex justify-between items-center bg-white sticky top-0 z-10">
                            <h2 className="text-lg font-bold text-gray-900">Application Menu</h2>
                            <button onClick={() => setIsAppMenuOpen(false)} className="text-gray-900 p-2"><i className="fas fa-times text-2xl"></i></button>
                        </div>
                        <div className="p-4 overflow-y-auto flex-grow space-y-2">
                            <button onClick={() => { navigate('/'); setIsAppMenuOpen(false); }} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 text-gray-900 font-bold transition-colors w-full text-left"><i className="fas fa-home w-6"></i> Home</button>
                            <button onClick={() => { navigate('/equipment'); setIsAppMenuOpen(false); }} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 text-gray-900 font-bold transition-colors w-full text-left"><i className="fas fa-truck-monster w-6"></i> Equipment</button>
                            <button onClick={() => { navigate('/consumables'); setIsAppMenuOpen(false); }} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 text-gray-900 font-bold transition-colors w-full text-left"><i className="fas fa-box-open w-6"></i> Consumables</button>
                            <button onClick={() => { navigate('/miscellaneous'); setIsAppMenuOpen(false); }} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 text-gray-900 font-bold transition-colors w-full text-left"><i className="fas fa-circle-notch w-6"></i> Miscellaneous</button>
                            <button onClick={() => { navigate('/rebuilds'); setIsAppMenuOpen(false); }} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 text-gray-900 font-bold transition-colors w-full text-left"><i className="fas fa-tools w-6"></i> Rebuilds</button>
                            <button onClick={() => { navigate('/rentals'); setIsAppMenuOpen(false); }} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 text-gray-900 font-bold transition-colors w-full text-left"><i className="fas fa-clock w-6"></i> Rentals</button>
                            <button onClick={() => { navigate('/commercial'); setIsAppMenuOpen(false); }} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 text-gray-900 font-bold transition-colors w-full text-left"><i className="fas fa-briefcase w-6"></i> Commercial</button>
                            <button onClick={() => { navigate('/components-and-spares'); setIsAppMenuOpen(false); }} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 text-gray-900 font-bold transition-colors w-full text-left"><i className="fas fa-cogs w-6"></i> Components</button>
                            <button onClick={() => { navigate('/financeform'); setIsAppMenuOpen(false); }} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 text-green-600 font-bold transition-colors w-full text-left"><i className="fas fa-hand-holding-usd w-6"></i> Financing</button>
                            <button onClick={() => { navigate('/sheq'); setIsAppMenuOpen(false); }} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 text-blue-600 font-bold transition-colors w-full text-left"><i className="fas fa-shield-alt w-6"></i> SHEQ Services</button>
                            <button onClick={() => { navigate('/kp-insurance'); setIsAppMenuOpen(false); }} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 text-red-600 font-bold transition-colors w-full text-left"><i className="fas fa-crown w-6"></i> King Price</button>
                            <button onClick={() => { navigate('/labour'); setIsAppMenuOpen(false); }} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 text-green-600 font-bold transition-colors w-full text-left"><i className="fas fa-users w-6"></i> Labour</button>
                            <button onClick={() => { navigate('/drill-catalog'); setIsAppMenuOpen(false); }} className="flex items-center gap-3 p-3 rounded-lg bg-orange-50 text-orange-600 font-bold transition-colors w-full text-left"><i className="fas fa-drill w-6"></i> Drill Catalog</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Drill Catalog Content */}
            <div className="container mx-auto px-4 py-8">
                <div className="max-w-4xl mx-auto">
                    {/* View Catalog PDF Button */}
                    <div className="mb-8">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-orange-600 rounded-full animate-pulse"></div>
                            <span className="text-[12px] font-black text-gray-900 uppercase tracking-tighter">Drill Catalog</span>
                        </div>
                        <button
                            type="button"
                            onClick={() => setPdfModalOpen(true)}
                            className="flex items-center gap-3 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors font-bold"
                        >
                            <i className="fas fa-file-pdf text-xl"></i>
                            View Drill Catalog PDF
                        </button>
                    </div>

                    {/* PDF Modal */}
                    {pdfModalOpen && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
                            <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col">
                                <div className="flex justify-between items-center p-4 border-b">
                                    <h3 className="text-lg font-bold text-gray-900">Drill Catalog</h3>
                                    <button onClick={() => setPdfModalOpen(false)} className="text-gray-900 p-2"><i className="fas fa-times text-2xl"></i></button>
                                </div>
                                <div className="flex-grow p-4">
                                    <iframe
                                        src="/MM_Drills/MinersMarket_Drills.pdf"
                                        className="w-full h-full rounded-lg border border-gray-200"
                                        title="Drill Catalog PDF"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Contact Information Form */}
                    <div className="bg-white rounded-lg shadow-lg p-6 mb-8 border border-gray-200">
                        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b">Contact Information</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={contactInfo.name}
                                    onChange={handleContactChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                                <input
                                    type="text"
                                    name="company"
                                    value={contactInfo.company}
                                    onChange={handleContactChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={contactInfo.email}
                                    onChange={handleContactChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={contactInfo.phone}
                                    onChange={handleContactChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Additional Message</label>
                            <textarea
                                name="message"
                                value={contactInfo.message}
                                onChange={handleContactChange}
                                rows="3"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Any additional information or requirements..."
                            />
                        </div>
                    </div>

                    {/* Drill Categories - Collapsible Sections */}
                    {isLoadingData ? (
                        <div className="text-center py-8">
                            <i className="fas fa-spinner fa-spin text-3xl text-blue-600 mb-4"></i>
                            <p className="text-gray-600">Loading drill catalog...</p>
                        </div>
                    ) : (
                        <div className="space-y-4 mb-8">
                            {drillCategories.map((category, index) => {
                                const isOpen = openSections[index];
                                const categorySelections = localSelections[category.name] || {};
                                const hasSelections = Object.keys(categorySelections).length > 0;

                                return (
                                    <div key={index} className="border border-gray-200 rounded-lg">
                                        <button
                                            type="button"
                                            className="w-full flex justify-between items-center p-4 text-left font-semibold text-gray-800 bg-gray-50 hover:bg-gray-100"
                                            onClick={() => toggleSection(index)}
                                        >
                                            <span>{category.name}</span>
                                            <i className={`fas fa-chevron-down transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
                                        </button>
                                        {isOpen && (
                                            <div className="p-4 bg-white">
                                                <div className="mb-4">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Product</label>
                                                    <div className="relative">
                                                        <button
                                                            type="button"
                                                            onClick={() => setDropdownOpen(prev => ({ ...prev, [category.name]: !prev[category.name] }))}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-left bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between"
                                                        >
                                                            <span className="text-gray-500">Choose a product...</span>
                                                            <i className="fas fa-chevron-down text-gray-400"></i>
                                                        </button>
                                                        {dropdownOpen[category.name] && (
                                                            <div data-dropdown={category.name} className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                                                                <div className="p-2 border-b border-gray-100">
                                                                    <input
                                                                        type="text"
                                                                        value={dropdownSearch[category.name] || ''}
                                                                        onChange={(e) => setDropdownSearch(prev => ({ ...prev, [category.name]: e.target.value }))}
                                                                        placeholder="Search products..."
                                                                        className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                        autoFocus
                                                                    />
                                                                </div>
                                                                <div className="overflow-y-auto max-h-48">
                                                                    {category.items
                                                                        .filter(item => item.toLowerCase().includes((dropdownSearch[category.name] || '').toLowerCase()))
                                                                        .map((item, itemIndex) => {
                                                                            const imgSrc = getDrillImagePath(item);
                                                                            const isSelected = categorySelections.hasOwnProperty(item);
                                                                            return (
                                                                                <div
                                                                                    key={itemIndex}
                                                                                    onClick={() => !isSelected && handleDropdownChange(category.name, item)}
                                                                                    className={`flex items-center gap-3 p-2 cursor-pointer hover:bg-blue-50 ${isSelected ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}
                                                                                >
                                                                                    {imgSrc ? (
                                                                                        <img
                                                                                            src={imgSrc}
                                                                                            alt={item}
                                                                                            className="w-10 h-10 object-contain bg-white border border-gray-200 rounded"
                                                                                             onError={handleImageError}
                                                                                        />
                                                                                    ) : (
                                                                                        <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs">No img</div>
                                                                                    )}
                                                                                    <span className="text-sm text-gray-700 flex-1">{item}</span>
                                                                                    {isSelected && <span className="text-xs text-green-600 font-bold">Added</span>}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    {category.items.filter(item => item.toLowerCase().includes((dropdownSearch[category.name] || '').toLowerCase())).length === 0 && (
                                                                        <div className="p-4 text-center text-gray-500 text-sm">No products found</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    {category.items.map((item, itemIndex) => {
                                                        const isSelected = categorySelections.hasOwnProperty(item);
                                                        const quantity = categorySelections[item] || 1;
                                                        const imgSrc = getDrillImagePath(item);

                                                        if (!isSelected) return null;

                                                        return (
                                                            <div key={itemIndex} className="flex items-center justify-between p-3 border border-blue-500 bg-blue-50/30 rounded-lg">
                                                                <div className="flex items-center gap-3 flex-1">
                                                                    {imgSrc && (
                                                                        <img
                                                                            src={imgSrc}
                                                                            alt={item}
                                                                            className="w-12 h-12 object-contain bg-white border border-gray-200 rounded"
                                                                             onError={handleImageError}
                                                                        />
                                                                    )}
                                                                    <span className="text-sm text-gray-700 flex-1">{item}</span>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <button type="button" onClick={() => handleQuantityChange(category.name, item, -1)} className="text-blue-600 hover:text-blue-800 disabled:opacity-30" disabled={quantity <= 1}><i className="fas fa-minus-circle"></i></button>
                                                                    <span className="text-sm text-black w-8 text-center">{quantity}</span>
                                                                    <button type="button" onClick={() => handleQuantityChange(category.name, item, 1)} className="text-blue-600 hover:text-blue-800 disabled:opacity-30" disabled={quantity >= MAX_QUANTITY_PER_ITEM}><i className="fas fa-plus-circle"></i></button>
                                                                    <button type="button" onClick={() => handleRemoveItem(category.name, item)} className="text-red-600 hover:text-red-800 ml-2"><i className="fas fa-trash"></i></button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Add & View Basket Button */}
                    {totalSelections > 0 && (
                        <div className="mt-8 text-center">
                            <button
                                onClick={handleAddAllToBasket}
                                disabled={!isContactValid}
                                className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white font-bold text-lg rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                <i className="fas fa-shopping-basket"></i>
                                Add {totalSelections} Item(s) & View Basket
                            </button>
                            {!isContactValid && (
                                <p className="text-sm text-red-600 mt-2">Please fill in Name and Email to enable</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DrillCatalogPage